import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { ReactComponent as Burger } from "../../assets/svg/burger.svg";
import { parseBody, parseInline } from "./parseBody";

/**
 * 결과지를 이미지 대신 글로 그리는 템플릿.
 *
 * 기존 결과 이미지(폭 794px)를 실측한 값을 그대로 쓰고,
 * 모든 크기를 "794 기준 단위(u)"로 적어 화면 폭에 맞춰 비례 축소합니다.
 * 그래서 어느 화면에서든 기존 결과 이미지와 같은 비율로 보입니다.
 */

const BASE = 794;
const u = (n) => `calc(var(--u) * ${n})`;

const BODY_LINE = 43; // 본문 줄 간격
const GAP = {
  paragraph: BODY_LINE, // 문단 사이 = 빈 줄 하나
  afterHeading: 0,
  image: 18,
  divider: 35,
  headingAfterDivider: 16,
  heading: 97,
  firstBlock: 27, // 제목 아래 선 → 첫 문단
};

const marginBefore = (block, prev) => {
  if (!prev) return GAP.firstBlock;
  switch (block.type) {
    case "heading":
      return prev.type === "divider" ? GAP.headingAfterDivider : GAP.heading;
    case "divider":
      return GAP.divider;
    case "image":
      return prev.type === "divider" ? GAP.headingAfterDivider : GAP.image;
    case "space":
      return 0;
    default:
      if (prev.type === "heading") return GAP.afterHeading;
      if (prev.type === "image") return GAP.image;
      if (prev.type === "divider") return GAP.headingAfterDivider;
      return GAP.paragraph;
  }
};

const Inline = ({ line }) =>
  parseInline(line).map((part, i) => {
    if (part.mark) return <Mark key={i}>{part.text}</Mark>;
    if (part.bold) return <strong key={i}>{part.text}</strong>;
    return <React.Fragment key={i}>{part.text}</React.Fragment>;
  });

const Block = ({ block, images, style }) => {
  switch (block.type) {
    case "heading":
      return (
        <Heading style={style}>
          <Inline line={block.text} />
        </Heading>
      );
    case "divider":
      return <Divider style={style} />;
    case "space":
      return <div style={{ height: u(block.lines * BODY_LINE) }} />;
    case "image": {
      const src = images?.[block.key];
      if (!src) return null;
      return <Picture style={style} src={src} alt={block.alt} width={block.width} />;
    }
    default:
      return (
        <Paragraph style={style}>
          {block.lines.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              <Inline line={line} />
            </React.Fragment>
          ))}
        </Paragraph>
      );
  }
};

/**
 * 이미지는 "폭 794 문서 기준"의 원본 픽셀 크기 그대로 비례 축소해서 보여줍니다.
 * (예: 폭 393px 그림 → 문서 폭의 393/794). 폭을 직접 정하려면 ![설명](키 50%)
 */
const Picture = ({ src, alt, width, style }) => {
  const [natural, setNatural] = useState(null);
  const w = width || (natural ? u(natural) : undefined);
  return (
    <Figure
      src={src}
      alt={alt}
      style={{ ...style, width: w, visibility: w ? "visible" : "hidden" }}
      onLoad={(e) => setNatural(e.currentTarget.naturalWidth)}
    />
  );
};

export const ResultDocument = ({ test, result }) => {
  const blocks = useMemo(() => {
    const list = [];
    if (result.subtitle) {
      list.push({ type: "paragraph", lines: result.subtitle.trim().split("\n") });
    }
    return list.concat(parseBody(result.body));
  }, [result]);

  return (
    <Page>
      <Sheet>
        <Header>
          <span>{test.title}</span>
          <span>바싹 마른 망고</span>
        </Header>
        <Rule style={{ marginTop: u(32.4), marginLeft: u(10), marginRight: u(18) }} />
        <Title>{result.type}</Title>
        <Rule style={{ marginTop: u(36), marginLeft: u(8), marginRight: u(22) }} />

        {blocks.map((block, i) => (
          <Block
            key={i}
            block={block}
            images={result.images}
            style={{ marginTop: u(marginBefore(block, blocks[i - 1])) }}
          />
        ))}

        <Rule thin style={{ marginTop: u(140), marginLeft: u(14), marginRight: u(16) }} />
        <Footer>
          Practical Moist 템플릿은,
          <br />
          좌측 상단 Menu 버튼에 있습니다.
          <FooterIcon aria-hidden="true" />
        </Footer>
      </Sheet>
    </Page>
  );
};

/* ---------- 스타일 (단위: 794 기준) ---------- */

// 결과 영역 폭을 기준으로 비례 축소
const Page = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 24px auto;
  container-type: inline-size;
`;

const Sheet = styled.article`
  --u: calc(100cqw / ${BASE});
  position: relative;
  padding-top: ${u(105)};
  padding-bottom: ${u(85)};
  background: #fff;
  color: #171515;
  font-family: "Gyeonggi Batang", "GyeonggiBatang", serif;
  font-weight: 400;
  letter-spacing: 0;
  text-align: left;

  /* 컨테이너 쿼리를 지원하지 않는 구형 브라우저: 레이아웃 폭으로 계산 */
  @supports not (width: 1cqw) {
    --u: calc((min(100vw, 448px) - 34px) / ${BASE});
  }

  strong {
    font-weight: 700;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 ${u(35)} 0 ${u(23)};
  font-size: ${u(34.6)};
  line-height: 1;
  font-weight: 700;
`;

const Rule = styled.hr`
  height: ${(p) => u(p.thin ? 1 : 2)};
  margin-bottom: 0;
  border: 0;
  background: #171515;
`;

const Title = styled.h1`
  margin: ${u(31.2)} ${u(23)} 0 ${u(8)};
  font-size: ${u(60.7)};
  line-height: 1;
  font-weight: 400;
  text-align: center;
`;

const Paragraph = styled.p`
  margin: 0;
  padding: 0 ${u(28.5)} 0 ${u(28)};
  font-size: ${u(33.7)};
  line-height: ${u(BODY_LINE)};
  text-align: justify;
  text-justify: inter-word;
  word-break: break-all;
`;

const Heading = styled.h2`
  margin: 0;
  padding: 0 ${u(28.5)} 0 ${u(28)};
  font-size: ${u(40)};
  line-height: ${u(50)};
  font-weight: 700;
  word-break: keep-all;
`;

// 원본: 높이 2px, 6px 선 + 1px 틈의 촘촘한 회색 점선, 좌우 끝까지
const Divider = styled.div`
  height: ${u(2)};
  background: repeating-linear-gradient(
    90deg,
    #b6b5b5 0,
    #b6b5b5 ${u(6)},
    transparent ${u(6)},
    transparent ${u(7)}
  );
`;

const Figure = styled.img`
  display: block;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
`;

// 형광펜: 글자 바로 뒤에 붙는 #FADEAC 띠 (원본 실측: 글자 윗선 +2, 높이 35)
const Mark = styled.span`
  padding-bottom: ${u(2.6)};
  background: linear-gradient(#fadeac, #fadeac) 0 ${u(2.3)} / 100% 100% no-repeat;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
`;

const Footer = styled.div`
  position: relative;
  margin: ${u(112)} ${u(8)} 0 0;
  font-size: ${u(31.4)};
  line-height: ${u(48)};
  text-align: center;
`;

const FooterIcon = styled(Burger)`
  position: absolute;
  left: ${u(665.8)};
  top: ${u(8.6)};
  width: ${u(76.5)};
  height: ${u(76.5)};
`;
